import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Loader2, Plus, Trash2, Save, Truck } from "lucide-react";

export default function SicurezzaCosts() {
  const [base, setBase] = useState(200);
  const [automezzi, setAutomezzi] = useState(200);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/sicurezza").then((r) => {
      setBase(r.data.base ?? 200);
      setAutomezzi(r.data.automezzi_eur_giorno ?? 200);
      setTiers(r.data.tiers || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => setTiers((t) => t.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = () => setTiers((t) => [...t, { da_kw: "", a_kw: "", prezzo: "" }]);
  const removeRow = (i) => setTiers((t) => t.filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = {
      base: Number(base),
      automezzi_eur_giorno: Number(automezzi),
      tiers: tiers.map((t) => ({
        da_kw: Number(t.da_kw),
        a_kw: t.a_kw === "" || t.a_kw === null || t.a_kw === undefined ? null : Number(t.a_kw),
        prezzo: Number(t.prezzo),
      })),
    };
    if (Number.isNaN(payload.base) || Number.isNaN(payload.automezzi_eur_giorno) ||
        payload.tiers.some((t) => Number.isNaN(t.da_kw) || Number.isNaN(t.prezzo) || (t.a_kw !== null && Number.isNaN(t.a_kw)))) {
      toast.error("Compila base, automezzi e le fasce (lascia 'A' vuoto per l'ultima fascia illimitata)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/sicurezza", payload);
      setBase(data.base); setAutomezzi(data.automezzi_eur_giorno); setTiers(data.tiers);
      toast.success("Listino sicurezza salvato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Sicurezza & Automezzi</h1>
          <p className="text-xs text-muted-foreground">Quota base + importo a corpo per fascia di potenza; automezzi €/giorno di cantiere</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-sicurezza-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva listino
        </Button>
      </header>

      <div className="p-4 lg:p-8 space-y-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <Card className="rounded border-border p-5" data-testid="sicurezza-card">
              <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" strokeWidth={1.5} /> Sicurezza
              </h2>
              <p className="text-xs text-muted-foreground mb-5">Importo <b>a corpo</b> applicato al preventivo (lead e progetti) = <b>quota base + importo di fascia</b> in funzione della potenza impianto. Lascia il campo <b>"A"</b> vuoto nell'ultima fascia per "da … in su".</p>

              <div className="mb-5 max-w-[220px]">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Quota base (€)</Label>
                <Input type="number" value={base} onChange={(e) => setBase(e.target.value)} data-testid="sicurezza-base-input" placeholder="200" className="mt-1" />
              </div>

              <div className="hidden sm:grid grid-cols-[140px_140px_160px_40px] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <div>Da (kW)</div><div>A (kW)</div><div>Importo a corpo (€)</div><div></div>
              </div>
              <div className="space-y-2">
                {tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[140px_140px_160px_40px] gap-3 items-center" data-testid={`sicurezza-row-${i}`}>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Da (kW)</Label>
                      <Input type="number" value={t.da_kw} onChange={(e) => setRow(i, "da_kw", e.target.value)} data-testid={`sicurezza-da-${i}`} placeholder="0" />
                    </div>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">A (kW)</Label>
                      <Input type="number" value={t.a_kw ?? ""} onChange={(e) => setRow(i, "a_kw", e.target.value)} data-testid={`sicurezza-a-${i}`} placeholder="in su" />
                    </div>
                    <div className="space-y-1 sm:space-y-0">
                      <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Importo (€)</Label>
                      <Input type="number" value={t.prezzo} onChange={(e) => setRow(i, "prezzo", e.target.value)} data-testid={`sicurezza-prezzo-${i}`} placeholder="500" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeRow(i)} data-testid={`sicurezza-remove-${i}`} className="text-destructive hover:text-destructive justify-self-end col-span-2 sm:col-span-1">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="add-sicurezza-btn" className="mt-4">
                <Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia
              </Button>
            </Card>

            <Card className="rounded border-border p-5" data-testid="automezzi-card">
              <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" strokeWidth={1.5} /> Automezzi
              </h2>
              <p className="text-xs text-muted-foreground mb-4">Calcolati come <b>€/giorno × giorni di cantiere</b> (i giorni derivano dal preventivo).</p>
              <div className="max-w-[220px]">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tariffa (€/giorno)</Label>
                <Input type="number" value={automezzi} onChange={(e) => setAutomezzi(e.target.value)} data-testid="automezzi-input" placeholder="200" className="mt-1" />
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
